/**
 * DEMO DATA - content models carried over from the live product
 * ---------------------------------------------------------------------------
 * Shapes taken from omnimind.elama.ai: the daily intelligence report and its
 * tabs, the report feed, document upload, the eLama source toggles, the
 * anomaly feed, period comparison, the delivery funnel and the admin screens.
 */

/* ------------------------------------------- daily intelligence report */

export const INTELLIGENCE_REPORT = {
  title: 'Your Daily Intelligence Report',
  confidence: 86,
  categoriesAnalysed: 12,
  summary:
    'Your daily briefing is ready. Today it shows 1 positive trend alongside 2 areas needing attention, and 2 actions worth taking this week.',
  metrics: [
    { label: 'Revenue QTD', value: '$14.8M', context: 'Finance' },
    { label: 'Gross margin', value: '37.6%', context: 'Finance' },
    { label: 'Open pipeline', value: '$42M', context: 'Sales' },
    { label: 'Cash position', value: '$8.1M', context: 'Finance' },
    { label: 'Overdue > 60 days', value: '$7.6M', context: 'Finance' },
    { label: 'Deals closing Q4', value: '31', context: 'Sales' },
    { label: 'Headcount', value: '612', context: 'People' },
    { label: 'Active sources', value: '4 of 7', context: 'Platform' },
  ],
  positives: [
    {
      title: 'KSA pipeline crossed the $12M threshold',
      severity: 'HIGH',
      category: 'Sales',
      body: '$14.2M of qualified pipeline, up 62% year on year, passing the trigger your 2027 strategy set for a local presence.',
    },
  ],
  attention: [
    {
      title: 'Enterprise discounting is eroding margin',
      severity: 'HIGH',
      category: 'Finance',
      body: 'Average discount on deals above $250K reached 19% against a 13% plan. Fourteen deals cleared the threshold without a recorded approval.',
      action: 'Review deal-desk enforcement and confirm the Q3 discount cap with Sales.',
    },
    {
      title: 'Cairo Fintech Group is 96 days overdue',
      severity: 'INFO',
      category: 'Finance',
      body: '$1.2M outstanding with no payment plan in any system, yet the 13-week cash model still assumes November collection.',
      action: 'Escalate to the controller and remove the assumption from the central case.',
    },
  ],
  actions: [
    {
      n: 1,
      priority: 'Important',
      tone: 'a',
      category: 'Finance',
      text: 'Review enterprise discounting and reinstate the deal-desk threshold.',
    },
    {
      n: 2,
      priority: 'Normal',
      tone: 'b',
      category: 'Sales',
      text: 'Confirm the top three KSA deals before committing to the Riyadh case.',
    },
  ],
};

/* --------------------------------------------- report history (richer) */

export const REPORT_STATS = { total: 131, unread: 17, critical: 3, highlyRated: 24 };

export const REPORT_FEED = [
  {
    day: 'Today',
    date: '27 Sep 2026',
    unread: false,
    positive: 1,
    concerns: 2,
    actions: 2,
    confidence: 86,
    categories: ['Finance', 'Sales', 'Invoices', 'Products'],
    more: 8,
    summary: 'Margin slipped for a third week; discounting on enterprise deals is the driver, not cost.',
  },
  {
    day: 'Yesterday',
    date: '26 Sep 2026',
    unread: true,
    positive: 2,
    concerns: 1,
    actions: 3,
    confidence: 88,
    categories: ['Sales', 'Pipeline', 'Contacts'],
    more: 9,
    summary: 'KSA pipeline crossed the threshold your 2027 strategy set for a local presence.',
  },
  {
    day: 'Fri, 25 Sep',
    date: '25 Sep 2026',
    unread: true,
    positive: 1,
    concerns: 2,
    actions: 1,
    confidence: 81,
    categories: ['Finance', 'Invoices'],
    more: 10,
    summary: 'Collections improved week on week, but inbound freight per unit remains elevated.',
  },
  {
    day: 'Thu, 24 Sep',
    date: '24 Sep 2026',
    unread: false,
    positive: 0,
    concerns: 3,
    actions: 2,
    confidence: 79,
    categories: ['Finance', 'Sales'],
    more: 8,
    summary: 'Two systems disagree on Q2 revenue by $180K - the board pack should cite SAP.',
  },
  {
    day: 'Wed, 23 Sep',
    date: '23 Sep 2026',
    unread: false,
    positive: 1,
    concerns: 1,
    actions: 2,
    confidence: 84,
    categories: ['Finance', 'Receivables'],
    more: 7,
    summary: 'Cairo Fintech Group passed 90 days overdue with no payment plan recorded.',
  },
  {
    day: 'Tue, 22 Sep',
    date: '22 Sep 2026',
    unread: false,
    positive: 2,
    concerns: 0,
    actions: 1,
    confidence: 90,
    categories: ['People', 'Workday'],
    more: 6,
    summary: 'Headcount plan on track and attrition flat for the fourth consecutive month.',
  },
];

/* ------------------------------------------------ uploaded documents */

export const UPLOAD_ACCEPT = 'PDF, Word, Excel, CSV, TXT, JSON · max 50MB';

export const UPLOADED_DOCS = [
  { name: 'Strategy_2027.pdf', type: 'PDF', size: '4.2 MB', added: '2h ago', status: 'Indexed', tone: 'g', pages: 64 },
  { name: 'Budget_FY26_v8.xlsx', type: 'Excel', size: '1.1 MB', added: '5h ago', status: 'Uncertified', tone: 'a', pages: 18 },
  { name: 'NileHolding_MSA.pdf', type: 'PDF', size: '2.8 MB', added: 'Yesterday', status: 'Indexed', tone: 'g', pages: 41 },
  { name: 'Q3_Forecast.csv', type: 'CSV', size: '318 KB', added: 'Yesterday', status: 'Indexed', tone: 'g', pages: 1 },
  { name: 'Freight_Contract_2026.docx', type: 'Word', size: '760 KB', added: '2 days ago', status: 'Needs review', tone: 'r', pages: 22 },
];

/* ----------------------------------------------------- eLama sources */

export const ELAMA_SOURCES = [
  { id: 'calls', name: 'Call Analytics', blurb: 'AI-powered call analytics', on: true },
  { id: 'social', name: 'Social Listening', blurb: 'Social media insights', on: false },
  { id: 'video', name: 'Video AI', blurb: 'Video analytics', on: false },
];

/* -------------------------------------------------- anomaly feed */

export const ANOMALY_FEED = [
  { severity: 'critical', tone: 'r', type: 'threshold_breach', status: 'active', metric: 'error_rate', message: 'Error rate exceeded - 8.5% in the last hour', expected: '2.00', actual: '8.50', delta: '+325.0%', detected: 'Today, 07:56' },
  { severity: 'high', tone: 'r', type: 'threshold_breach', status: 'active', metric: 'discount_rate_enterprise', message: 'Enterprise discount rate above the deal-desk threshold', expected: '12.50', actual: '19.00', delta: '+52.0%', detected: 'Today, 09:12' },
  { severity: 'high', tone: 'a', type: 'threshold_breach', status: 'active', metric: 'query_latency_ms', message: 'Query latency spike - 7.2s average response time', expected: '2000.00', actual: '7200.00', delta: '+260.0%', detected: 'Today, 09:56' },
  { severity: 'medium', tone: 'a', type: 'statistical', status: 'acknowledged', metric: 'days_sales_outstanding', message: 'DSO 24% above its normal range', expected: '55.00', actual: '68.00', delta: '+23.6%', detected: 'Yesterday, 18:20' },
  { severity: 'medium', tone: 'a', type: 'statistical', status: 'active', metric: 'active_users', message: 'User sessions 65% below normal', expected: '100.00', actual: '35.00', delta: '-65.0%', detected: 'Yesterday, 05:56' },
  { severity: 'low', tone: 'n', type: 'statistical', status: 'acknowledged', metric: 'api_calls', message: 'API calls dropped 44%', expected: '5000.00', actual: '2800.00', delta: '-44.0%', detected: '2 days ago' },
];

/* --------------------------------------------- period comparison */

export const PERIOD_COMPARE = {
  weeks: [
    { week: 'Week 1', current: 412, previous: 356 },
    { week: 'Week 2', current: 468, previous: 390 },
    { week: 'Week 3', current: 496, previous: 402 },
    { week: 'Week 4', current: 484, previous: 402 },
  ],
  metrics: [
    { label: 'Total questions', now: '1,860', prev: '1,550', delta: '+18.2%', dir: 'up' },
    { label: 'Response time', now: '245ms', prev: '280ms', delta: '-12.5%', dir: 'dn' },
    { label: 'Answer satisfaction', now: '4.2 / 5', prev: '3.9 / 5', delta: '+0.3', dir: 'up' },
  ],
};

/* ---------------------------------------------- report delivery funnel */

export const DELIVERY = [
  { stage: 'Sent', count: 1042 },
  { stage: 'Delivered', count: 1036 },
  { stage: 'Opened', count: 708 },
  { stage: 'Clicked', count: 412 },
  { stage: 'Bounced', count: 6 },
];

export const VALUED_INSIGHTS = [
  { insight: 'Enterprise discounting above plan', rated: 38, acted: 21 },
  { insight: 'Overdue receivable concentration', rated: 31, acted: 18 },
  { insight: 'KSA pipeline threshold crossed', rated: 27, acted: 9 },
  { insight: 'Revenue reconciliation conflict', rated: 22, acted: 12 },
];

/* ------------------------------------------- executive year comparison */

export const YOY = {
  years: ['2026', '2025', '2024', '2023'],
  current: { year: '2026', revenue: '$14.8M', deals: 287, avgDeal: '$460K' },
  previous: { year: '2025', revenue: '$12.4M', deals: 262, avgDeal: '$448K' },
  performers: [
    { rank: 1, name: 'Wei Anderson', team: 'Enterprise', deals: 9, value: '$2.78M', commission: '$110K' },
    { rank: 2, name: 'Maria Brown', team: 'Enterprise', deals: 8, value: '$2.61M', commission: '$87K' },
    { rank: 3, name: 'John Kang', team: 'Mid-market', deals: 11, value: '$2.48M', commission: '$79K' },
    { rank: 4, name: 'Miguel Liu', team: 'Enterprise', deals: 7, value: '$2.14M', commission: '$77K' },
    { rank: 5, name: 'Neha Moore', team: 'Mid-market', deals: 10, value: '$2.11M', commission: '$94K' },
  ],
};

/* ------------------------------------------------------ admin screens */

export const USERS = [
  { name: 'John Smith', email: 'ceo@demo.omnimind.local', role: 'CEO', department: 'Executive', status: 'Active', tone: 'g', lastSeen: '2m ago', questions: 312 },
  { name: 'Salma Adel', email: 'cfo@demo.omnimind.local', role: 'CFO', department: 'Finance', status: 'Active', tone: 'g', lastSeen: '18m ago', questions: 486 },
  { name: 'Yara Rostom', email: 'cro@demo.omnimind.local', role: 'CRO', department: 'Sales', status: 'Active', tone: 'g', lastSeen: '1h ago', questions: 298 },
  { name: 'Omar Shafik', email: 'controller@demo.omnimind.local', role: 'Manager', department: 'Finance', status: 'Active', tone: 'g', lastSeen: '3h ago', questions: 142 },
  { name: 'Hisham Fouad', email: 'chro@demo.omnimind.local', role: 'CHRO', department: 'People', status: 'Active', tone: 'g', lastSeen: 'Yesterday', questions: 46 },
  { name: 'Tarek Ghanem', email: 'analyst@demo.omnimind.local', role: 'Analyst', department: 'Finance', status: 'Invited', tone: 'a', lastSeen: 'Never', questions: 0 },
  { name: 'Mona Fathy', email: 'board@demo.omnimind.local', role: 'Board member', department: 'Executive', status: 'Suspended', tone: 'r', lastSeen: '3 weeks ago', questions: 18 },
];

export const DEPARTMENTS = [
  { name: 'Executive', members: 24, sources: 7, categories: 12, lead: 'John Smith' },
  { name: 'Finance', members: 58, sources: 5, categories: 9, lead: 'Salma Adel' },
  { name: 'Sales', members: 72, sources: 3, categories: 6, lead: 'Yara Rostom' },
  { name: 'Operations', members: 48, sources: 4, categories: 7, lead: 'Karim Nabil' },
  { name: 'People', members: 40, sources: 2, categories: 4, lead: 'Hisham Fouad' },
];

export const PERM_LEVELS = ['full', 'read', 'aggregate', 'none'] as const;

export const CATEGORY_PERMISSIONS = [
  { category: 'Revenue & invoices', exec: 'full', finance: 'full', sales: 'read', ops: 'none', people: 'none' },
  { category: 'Pipeline & forecast', exec: 'full', finance: 'read', sales: 'full', ops: 'none', people: 'none' },
  { category: 'Payroll & compensation', exec: 'aggregate', finance: 'read', sales: 'none', ops: 'none', people: 'full' },
  { category: 'Headcount & attrition', exec: 'full', finance: 'aggregate', sales: 'none', ops: 'read', people: 'full' },
  { category: 'Contracts & legal', exec: 'full', finance: 'read', sales: 'read', ops: 'read', people: 'none' },
  { category: 'Inventory & supply', exec: 'read', finance: 'read', sales: 'none', ops: 'full', people: 'none' },
];

export const ACCESS_REQUESTS = [
  { who: 'Tarek Ghanem', role: 'Analyst', asks: 'Payroll & compensation', reason: 'Building the FY27 cost model', when: '2h ago', status: 'Pending', tone: 'a' },
  { who: 'Yara Rostom', role: 'CRO', asks: 'Contracts & legal', reason: 'Checking price-increase caps before renewals', when: 'Yesterday', status: 'Pending', tone: 'a' },
  { who: 'Karim Nabil', role: 'Ops lead', asks: 'Revenue & invoices', reason: 'Reconciling freight recharges', when: '2 days ago', status: 'Approved', tone: 'g' },
  { who: 'Mona Fathy', role: 'Board member', asks: 'Payroll & compensation', reason: 'Board compensation review', when: '4 days ago', status: 'Declined', tone: 'r' },
];

export const SSO_CONFIG = {
  provider: 'Microsoft Entra ID',
  status: 'Connected',
  domain: 'elama.ai',
  users: 148,
  enforced: true,
  lastSync: '14m ago',
  settings: [
    { label: 'Protocol', value: 'SAML 2.0' },
    { label: 'Entity ID', value: 'urn:omnimind:elama' },
    { label: 'Reply URL', value: 'https://omnimind.elama.ai/auth/sso-callback' },
    { label: 'Certificate expires', value: '14 Mar 2027' },
    { label: 'Just-in-time provisioning', value: 'On' },
    { label: 'Default role for new users', value: 'Employee' },
  ],
};
